import { staffMorning } from './staff.ts';
import { legacyMorning } from './legacy.ts';
import { policyShare } from './policy.ts';
import { cheaper } from './prices.ts';
import { archiveNpcContacts, archiveNpcKin } from './kin.ts';
import { archiveNpcReactions } from './social.ts';
import { LAGOS } from './content/lagos.ts';
import { EDU_RANK, type Job, type RegionContent, type Schedule, type Skill } from './content/types.ts';
import { Rng } from './rng.ts';
import { deliverSocial, type SocialState } from './social.ts';
import { feedDay, type FeedState } from './feed.ts';
import { ageDay, businessDay, relationsDay, type Business } from './lifecycle.ts';
import { ventureDay } from './ventures.ts';
import { landDay } from './land.ts';
import { weddingDay } from './wedding.ts';
import { regionFor, fairPay, type LifeCard } from './spawn.ts';
import { bankDay, type BankState } from './places.ts';
import { abroad, homeCountry, travelDay } from './travel.ts';
import { paySalary } from './policy.ts';
import { voiceOf } from './voice.ts';
import { comfortOf, healHomesAbroad, type HomeStyle } from './housing.ts';
import { answerPromotion, careerOf, checkPromotion, reviewDay, type CareerState } from './careers.ts';
import { starterCar, type OwnedVehicle } from './vehicles.ts';
import { gigsDay, type BookedGig } from './opportunities.ts';
import { datesDay } from './dating.ts';
import { eventsDay } from './events.ts';
import { meetupsDay } from './meetups.ts';
import { classDay } from './school.ts';
import { owesDay } from './custody.ts';
import { deliverGroceries } from './groceries.ts';
import { deliverOrders } from './orders.ts';
import { carsDay } from './vehicles.ts';
import { petsDay } from './petcare.ts';
import { ASTRONAUT, ORBIT_SCHEDULE, afterTraining, inOrbit, spaceDay, type SpaceState } from './space.ts';
import { characterDay, characterTick, nudge, objectivePull, selfOf, type Chance, type Debt, type Later, type SelfState } from './character.ts';

/**
 * The life loop: one person's ongoing life after Start. Pure data + functions so it runs
 * headless (balance tests) and the game just renders it. Time is in game minutes from the start
 * of day 0 (a Monday). "Simulate everything; make the player experience only what is interesting":
 * this module simulates; the game decides what to show.
 */

export const START_DATE = Date.UTC(2026, 9, 12); // Monday 12 October 2026
const DAY = 1440;

export type NeedKey = 'energy' | 'hunger' | 'hygiene' | 'fun' | 'social';
export const NEEDS: { key: NeedKey; label: string; perHour: number }[] = [
  { key: 'energy', label: 'Energy', perHour: 4 },
  { key: 'hunger', label: 'Food', perHour: 3.9 }, // (5.5 had food eating most of a starting wage: players wrote in)
  { key: 'hygiene', label: 'Hygiene', perHour: 3 },
  { key: 'fun', label: 'Fun', perHour: 2.5 },
  { key: 'social', label: 'Social', perHour: 2 },
];

const LOW_LINES: Record<NeedKey, string> = {
  energy: 'You\'re exhausted. You need to sleep.',
  hunger: 'You\'re starving. Eat something soon.',
  hygiene: 'You need a wash. People are noticing.',
  fun: 'You\'re bored out of your mind.',
  social: 'You\'re lonely. Talk to someone.',
};

export type ObjectiveKind = 'buy_food' | 'talk' | 'work' | 'apply' | 'walk' | 'save' | 'call' | 'study' | 'rest' | 'explore' | 'practice' | 'cook' | 'post' | 'photo_asked' | 'help' | 'outing';
/** Where an objective came from: the daily system, someone in your life, your career, or the world. */
export type QuestSource = { type: 'daily' } | { type: 'npc'; name: string } | { type: 'career' } | { type: 'world' };
export interface Objective { id: string; kind: ObjectiveKind; text: string; source: QuestSource; target: number; progress: number; done: boolean; day: number }

export interface Txn { t: number; amount: number; label: string }
export interface Message { t: number; from: string; text: string; read: boolean; /** you wrote it (to `with`) */ mine?: boolean; with?: string; /** with a real player (their id): the sim never writes their side */ player?: string;
  /** a voice note (with a real player): its id on the server and how long it is */ vn?: { id: string; secs: number };
  /** money sent or received (with a real player), in the game's own units */ cash?: number;
  /** (with a real player) the message's id, the same on both phones: what a reaction points at */ mid?: string;
  /** a photo (with a real player): its id on the server */ pic?: string;
  /** reactions on it: yours, and theirs (an emoji each) */ react?: { me?: string; them?: string } }
export interface LifeEvent { t: number; kind: 'pay' | 'rent' | 'day' | 'missed' | 'fired' | 'message' | 'objective' | 'need' | 'info' | 'social' | 'death' | 'chance'; text: string; amount?: number }

export interface LifeState {
  /** the Astronaut Corps: training, a crew, the station */
  space?: SpaceState;
  /** money packs already in this life (their payment references: never paid in twice) */
  purchases?: string[];
  /** money transfers from other players already paid in (their ids: never twice) */
  cashIn?: string[];
  /** the Lagos pay rise has been applied to this life (upgradeLife) */
  payV2?: boolean;
  /** the start-in-life credit has been given to this life (startCredit) */
  boostV1?: boolean;
  /** someone you've invited over (visits.ts) */
  guest?: import('./visits.ts').Guest | null;
  /** paid countries this life can go to (Italy…) */
  unlocked?: string[];
  v: 1;
  card: LifeCard;
  minutes: number;
  money: number;
  needs: Record<NeedKey, number>;
  skills: Record<Skill, number>;
  health: number;
  ledger: Txn[];
  history: { t: number; text: string }[];
  messages: Message[];
  contacts: { name: string; relation: string; closeness: number; met: number; /** last time you were together */ seen?: number }[];
  /** real players you've swapped numbers with (kept apart from contacts: the sim never answers for a person) */
  players?: { id: string; name: string; met: number; /** 'Partner' once you've said yes to each other */ relation?: string }[];
  /** players who asked to add you as a contact (by your @username), waiting on your yes or no */
  contactReqs?: { id: string; name: string; handle: string; at: number; /** what they wrote before you accepted (a message request: two at most) */ msgs?: string[] }[];
  /** message requests you've sent: how many to each (two at most till they accept) */
  askTexts?: Record<string, number>;
  /** players you asked (their yes makes them a contact); who you said no to (they can't ask again for a day) */
  contactAsked?: { id: string; name: string; handle: string; at: number }[];
  contactNo?: { id: string; at: number }[];
  /** real players you follow (found by their @username): their posts come into your feed */
  following?: { id: string; name: string; handle: string; at: number }[];
  /** a trip with another player: who, where to, from which airport; yes once they've said yes (you board together) */
  tripWith?: { id: string; name: string; country: string; from: string; rid: string; yes: boolean; /** you offered to drive them to the airport */ pickup?: boolean } | null;
  objectives: Objective[];
  job: { id: string; label: string; place: string; incomeMonthly: number; rank?: number; baseLabel?: string; promotedAt?: number } | null;
  work: { worked: number; missed: number; streakMissed: number; performance: number; owed: number; lastShiftDay: number; /** on leave until (no shifts, no pay) */ leaveUntil?: number;
    /** you're at work right now: when you arrived and whether you were late */
    clockedIn?: { at: number; late: number } | null };
  rent: { yearly: number; dueDay: number; warned: boolean; /** a Lagos landlord's instalments: monthly, 10% on top (when the year at once was too much) */ monthly?: boolean } | null;
  dayStartMoney: number;
  seed: number;
  /** messages scheduled to arrive later */
  inbox: { at: number; from: string; text: string }[];
  /** who you've texted today (a text a day keeps closeness up) */
  texted?: string[];
  stats: { objectivesDone: number; strangersTalked: number; mealsBought: number; flocks?: number; gym?: number; prayers?: number; clubNights?: number; sprayed?: number; bottles?: number; groceryRuns?: number; yachtDays?: number; beachDays?: number; tvHours?: number; gamesPlayed?: number; poolGames?: number };
  /** tonight at the club (reset each night) */
  /** in a cell (arrested), waiting on a way out */
  custody?: import('./custody.ts').Custody;
  /** pictures you kept (the Gallery app) */
  gallery?: import('./social.ts').GalleryItem[];
  /** your pets (by kind), and portions of pet food left in the open bag */
  pets?: Partial<Record<import('./petcare.ts').PetKind, import('./petcare.ts').PetState>>;
  petFood?: number;
  /** scuba: the logbook (dives, deepest, creatures found, the Open Water card) */
  diveLog?: import('./diving.ts').DiveLog;
  /** a day at the beach (today's wristband) */
  beach?: import('./beaches.ts').BeachDay;
  /** out on a chartered yacht right now */
  charter?: import('./yachts.ts').Charter;
  /** the sunglasses you have on (an id from the game's range; owned ones are in `owned` as 'shades:<id>') */
  shades?: string;
  /** your kitchen: groceries bought and not used yet (id → how many) */
  pantry?: Record<string, number>;
  /** a child on the way with another player, and your children (family.ts) */
  expecting?: import('./family.ts').Expecting | null;
  children?: import('./family.ts').Child[];
  /** you asked this player about a child: their answer counts only against this */
  babyAsk?: { id: string; rid: string } | null;
  /** the cars that go with you when you drive (vehicles.ts) */
  convoy?: string[];
  /** players whose requests never reach you */
  blocked?: string[];
  /** you were in your own home, and where in it: you come back there after logging out, however long, on any device */
  atHome?: { hood: string; x: number; z: number; yaw: number; /** when (real time): the newest of this and the tab's note of where you were wins */ t?: number } | null;
  /** food you've bought and not eaten yet (food.ts) */
  meals?: import('./food.ts').FoodInstance[];
  /** grocery orders on their way */
  deliveries?: import('./groceries.ts').Delivery[];
  /** paintings you made (the art pack): hung at home or archived */
  paintings?: import('./activity.ts').Painting[];
  /** money a contact put up for you (bail): pay it back from Contacts */
  owes?: { name: string; amount: number; since: number; nagged?: boolean }[];
  /** dates booked (and how they went) */
  dates?: import('./dating.ts').DatePlan[];
  /** events you've booked (the Events app: a sip and paint…) */
  events?: import('./events.ts').EventBooking[];
  /** hang out later: plans with friends */
  meetups?: import('./meetups.ts').Meetup[];
  /** your course, while you're at university (school.ts) */
  uni?: import('./school.ts').Uni | null;
  /** your own clothes, kept in the locker while you're in theatre scrubs (null: you're in your own) */
  /** a tester's life (set by the owner, not in the game): every job open and taken on the spot, every requirement met */
  tester?: boolean;
  /** an engagement ring bought, waiting in your pocket (wedding.ts) */
  ring?: 'band' | 'gold' | 'diamond' | null;
  /** engaged: to whom, and the wedding once it's planned */
  engaged?: import('./wedding.ts').Engagement | null;
  workClothes?: { clothes: string[]; prints: Record<string, string>; accessories: string[]; hair: string | null } | null;
  /** the betting parlour: what you've staked and had back (betting.ts) */
  betting?: import('./betting.ts').Betting;
  /** a lawyer's days in court: cases won and lost */
  trials?: { won: number; lost: number };
  /** operations you've scrubbed in for (a doctor's theatre days) */
  surgeries?: number;
  club?: { night: number; drinks: number; vip: boolean; danced: number; sprayed?: number; bottles?: number; shouted?: boolean };
  lowWarned: NeedKey[];
  applications: { job: { id: string; label: string; place: string; incomeMonthly: number }; at: number }[];
  offers: { id: string; label: string; place: string; incomeMonthly: number; until: number }[];
  /** things you own that unlock activities (a keyboard…) */
  owned: string[];
  /** cars you own (vehicles.ts) */
  vehicles?: OwnedVehicle[];
  /** shifts worked per trade over the whole life (the jobs marketplace reads it) */
  experience?: Record<string, number>;
  /** gigs booked from the jobs marketplace */
  gigs?: BookedGig[];
  /** who gave you a lift on which day (you don't ask twice in a day) */
  lifts?: Record<string, number>;
  /** the outfit you put on (clothes ids), and the day you last dressed up */
  outfit: string[] | null;
  dressedDay: number;
  /** the account on your phone (created the first time it's used) */
  social: SocialState | null;
  /** the social feed: what the people you know post */
  feed?: FeedState;
  /** a business you own */
  business: Business | null;
  pregnancy: { due: number } | null;
  /** set when this life has ended */
  dead: { t: number; age: number; cause: string } | null;
  /** the lives before this one, in this family */
  lineage: { name: string; born: number; died: number; cause: string }[];
  /** who you're becoming: personality leans, what you've done (see character.ts) */
  self: SelfState | null;
  /** opportunities in front of you right now, waiting for an answer */
  chances: Chance[];
  /** consequences and arrivals scheduled for later */
  later: Later[];
  debts: Debt[];
  /** your bank: savings, a loan, your record */
  bank: BankState | null;
  /** how your home is done up, and whether you own it */
  homeStyle: HomeStyle | null;
  homeOwned: boolean;
  homeValue: number;
  /** certificates, a course in progress, a promotion waiting, reviews */
  career: CareerState | null;
  /** the guided first day (new lives only; older saves never get it) */
  guide?: GuideState;
}

/** Where a new life is in its guided first day, and what they chose along the way. */
export interface GuideState { step: string; picks: Record<string, string>; done: boolean; day: number; money0: number; did: string[] }

export const content = (s: LifeState): RegionContent => regionFor(s.card.region) ?? LAGOS;
export const dayOf = (t: number) => Math.floor(t / DAY);
export const weekday = (t: number) => dayOf(t) % 7; // 0 = Monday
export const hourOf = (t: number) => (t % DAY) / 60;
export const dateOf = (t: number) => new Date(START_DATE + t * 60_000);

/**
 * Money as this life sees it. The sim counts everything in one internal unit (naira); a life born
 * elsewhere sees its own currency (France: euros), converted at the currency's rate.
 */
export function fmtMoney(cur: { symbol: string; rate?: number }, n: number): string {
  const v = n / (cur.rate ?? 1);
  const abs = Math.abs(v), body = abs >= 100 || (cur.rate ?? 1) === 1 || Math.abs(abs - Math.round(abs)) < 0.005 ? Math.round(abs).toLocaleString('en-GB') : abs.toFixed(2); // (whole amounts without the .00)
  return `${v < 0 ? '−' : ''}${cur.symbol}${body}`;
}
export const money = (s: { card: { currency: { symbol: string; rate?: number } } }, n: number) => fmtMoney(s.card.currency, n);

export function clockLabel(t: number): string {
  const d = dateOf(t);
  const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()];
  const mo = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getUTCMonth()];
  return `${wd} ${d.getUTCDate()} ${mo} · ${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

export function schedule(s: LifeState): Schedule & { label: string } {
  const c = content(s);
  if (!s.job) return { ...(s.card.education.level === 'student' ? c.schedules.student : c.schedules.none), label: 'Classes' };
  if (inOrbit(s)) return { ...ORBIT_SCHEDULE, label: 'Timeline' }; // (on the station: every day, the timeline)
  // on leave: no shifts (none expected, none missed, none paid)
  if (s.work.leaveUntil && s.minutes < s.work.leaveUntil) return { ...(c.schedules[s.job.id] ?? c.schedules.default), days: [], label: 'Leave' };
  return { ...(c.schedules[s.job.id] ?? c.schedules.default), label: 'Shift' };
}

/**
 * The next shift (or class) you're expected at: when it starts, the last moment you can still turn
 * up for it, and whether today's is already done. For countdowns on screen.
 */
export function nextShift(s: LifeState): { start: number; lastChance: number; today: boolean } | null {
  const sc = schedule(s);
  if (!sc.days.length) return null;
  const today = dayOf(s.minutes);
  for (let k = 0; k < 8; k++) {
    const d = today + k;
    if (!sc.days.includes(d % 7)) continue;
    if (d === today && s.work.lastShiftDay === today) continue;
    const start = d * DAY + sc.start * 60, lastChance = d * DAY + (sc.end - 1) * 60;
    if (s.minutes > lastChance) continue;
    return { start, lastChance, today: k === 0 };
  }
  return null;
}

/** Is a shift (or class) on right now, and is there still enough of it left to go in? */
export function shiftNow(s: LifeState): { start: number; end: number } | null {
  const sc = schedule(s);
  if (!sc.days.includes(weekday(s.minutes))) return null;
  const h = hourOf(s.minutes);
  if (h < sc.start - 1 || h > sc.end - 1) return null;
  if (s.work.lastShiftDay === dayOf(s.minutes)) return null;
  return { start: sc.start, end: sc.end };
}

const rngFor = (s: LifeState, salt: number) => new Rng((s.seed ^ Math.imul(salt + 1, 0x9e3779b1)) >>> 0);

export function startLife(card: LifeCard): LifeState {
  const s: LifeState = {
    v: 1, card,
    minutes: 7 * 60 + 30,
    money: card.money,
    needs: { energy: 85, hunger: 60, hygiene: 80, fun: 55, social: 50 },
    skills: { ...card.skills },
    health: card.health,
    ledger: [], history: [], messages: [], payV2: true, boostV1: true,
    contacts: card.people.map((p) => ({ name: p.name, relation: p.relation, closeness: p.relation === 'Friend' ? 45 : 70, met: 0 })),
    objectives: [],
    job: card.job && card.job.id !== 'none' && card.job.id !== 'failed_biz' ? { ...card.job } : null,
    work: { worked: 0, missed: 0, streakMissed: 0, performance: 60, owed: 0, lastShiftDay: -1 },
    rent: card.home.rentYearly ? { yearly: card.home.rentYearly, dueDay: card.region === 'lagos' ? Math.round(card.home.rentDueMonths * 30.4) : (card.home.rentDueMonths * 7) % 30 + 1, warned: false } : null, // (monthly outside Lagos)
    dayStartMoney: card.money,
    seed: card.seed,
    inbox: [],
    stats: { objectivesDone: 0, strangersTalked: 0, mealsBought: 0 },
    lowWarned: [],
    applications: [],
    offers: [],
    owned: [],
    outfit: null,
    dressedDay: -1,
    social: null,
    business: null,
    pregnancy: null,
    dead: null,
    lineage: [],
    self: null,
    chances: [],
    later: [],
    debts: [],
    bank: null,
    homeStyle: null,
    homeOwned: false,
    homeValue: 0,
    career: null,
    guide: { step: 'phone', picks: {}, done: false, day: 0, money0: card.money, did: [] },
  };
  selfOf(s);
  starterCar(s);
  s.history.push({ t: s.minutes, text: `Started life in ${card.city} at ${card.age}, with ${fmtMoney(card.currency, card.money)}.` });
  startCredit(s);
  newDay(s, []);
  return s;
}

/** Bring a save from an older build up to date: any field added since gets its starting value. */
/** what every life has at least, to begin with (players couldn't find their feet: food, rides, a first rent) */
export const START_FLOOR = 1_000_000;
/**
 * A start in life: whatever you begin with, a credit alert tops it up to START_FLOOR, for a reason that fits the
 * life: family seeing you off (young), your savings group paying out your turn (an adult), help settling in (abroad).
 * Once per life (boostV1); lives already going get it when they next load.
 */
export function startCredit(s: LifeState) {
  const short = Math.ceil((START_FLOOR - s.money) / 1000) * 1000;
  if (short <= 0) return;
  const kin = s.contacts.find((c) => c.relation === 'Mother') ?? s.contacts.find((c) => c.relation === 'Father');
  const lagos = s.card.region === 'lagos';
  const [from, why] = !lagos ? ['Family back home', 'To help you settle in. Call us!']
    : s.card.stage === 'young' ? [kin ? `${kin.name}` : 'Your family', 'For your start in life. Spend it wisely!']
    : ['Your ajo (savings group)', "Your turn: this month's payout"];
  earn(s, short, `Credit: ${from}`);
  s.dayStartMoney = (s.dayStartMoney ?? 0) + short;
  if (s.guide) s.guide.money0 = (s.guide.money0 ?? 0) + short;
  s.messages.unshift({ t: s.minutes, from: 'Your bank', text: `Credit Alert: ${money(s, short)} from ${from}. "${why}" Available balance: ${money(s, s.money)}.`, read: false });
  if (lagos && s.card.stage === 'young' && kin) s.messages.unshift({ t: s.minutes, from: kin.name.split(' ')[0], text: 'I just sent you something. Use it well, and eat properly! ❤️', read: false });
  s.history.push({ t: s.minutes, text: `${from} sent ${money(s, short)}.` });
}

export function upgradeLife(saved: LifeState): LifeState {
  const fresh = startLife(saved.card), raise = !saved.payV2, boost = !saved.boostV1; // (before the new fields fill in: a new life has it already)
  for (const k of Object.keys(fresh) as (keyof LifeState)[]) {
    if (saved[k] === undefined) (saved as unknown as Record<string, unknown>)[k] = fresh[k];
  }
  // (the pay rise, once, for lives already going: the low-paid jobs up a third, the apprentices a stipend)
  if (boost) { saved.boostV1 = true; startCredit(saved); }
  healHomesAbroad(saved); // (a home abroad filed under the wrong country, put right)
  if (raise) { saved.payV2 = true; if (saved.job && saved.card.region === 'lagos') saved.job.incomeMonthly = fairPay(saved.job.incomeMonthly, saved.job.id); }
  archiveNpcKin(saved); // (the game's family and partners, archived: real players only)
  archiveNpcContacts(saved); // (and the game's people in your contacts: Contacts is real players)
  archiveNpcReactions(saved); // (and their likes and comments on Social: players' only)
  delete (saved as LifeState & { styling?: unknown }).styling; // (which home you were in: set again as you walk in)
  // (prices 30% down, prices.ts: a rent already agreed comes down with them, once)
  const sv = saved as LifeState & { prices70?: boolean };
  if (!sv.prices70) { sv.prices70 = true; if (saved.rent) saved.rent.yearly = cheaper(saved.rent.yearly, 10_000); }
  return saved;
}



// --- money ---------------------------------------------------------------------------------

export function earn(s: LifeState, amount: number, label: string) {
  s.money += amount;
  s.ledger.push({ t: s.minutes, amount, label });
}
export function spend(s: LifeState, amount: number, label: string): boolean {
  if (amount > s.money) return false;
  s.money -= amount;
  s.ledger.push({ t: s.minutes, amount: -amount, label });
  return true;
}

// --- time ----------------------------------------------------------------------------------

const clamp = (v: number) => Math.max(0, Math.min(100, v));

/** Let time pass. `asleep` restores energy instead of draining it. Returns what happened. */
export function advance(s: LifeState, minutes: number, opts: { asleep?: boolean; busy?: boolean } = {}): LifeEvent[] {
  const events: LifeEvent[] = [];
  archiveNpcContacts(s); // (anyone the game put in your contacts since: to the archive)
  let left = minutes;
  while (left > 0 && !s.dead) {
    const step = Math.min(left, 15, DAY - (s.minutes % DAY) || DAY); // never cross midnight inside a step
    const hours = step / 60;
    for (const n of NEEDS) {
      let rate = -n.perHour;
      if (opts.asleep) rate = n.key === 'energy' ? 12 : n.key === 'hunger' ? -1.5 : n.key === 'hygiene' ? -1 : 0;
      s.needs[n.key] = clamp(s.needs[n.key] + rate * hours);
    }
    // starving or exhausted for long wears health down; rested and fed restores it slowly
    if (s.needs.hunger < 10 || s.needs.energy < 5) s.health = clamp(s.health - 0.6 * hours);
    else if (s.needs.hunger > 50 && s.needs.energy > 40) s.health = clamp(s.health + 0.05 * hours);
    // say so when a need gets low (once, until it recovers)
    for (const n of NEEDS) {
      const v = s.needs[n.key];
      if (v < 20 && !s.lowWarned.includes(n.key)) {
        s.lowWarned.push(n.key);
        events.push({ t: s.minutes, kind: 'need', text: LOW_LINES[n.key] });
      } else if (v > 40) s.lowWarned = s.lowWarned.filter((k) => k !== n.key);
    }
    // push the body too far and it gives out: a clinic visit, a lost day, a bill
    // (no dying: a character never dies, by decision; a player lost every car and house to it. The body gives out to a clinic bed instead)
    if (s.health <= 3) {
      const fee = Math.min(s.money, Math.round(cheaper(15_000) * policyShare(homeCountry(s), 'clinic_fees')));
      spend(s, fee, 'Clinic');
      s.health = 35; s.needs.hunger = 55; s.needs.energy = 60;
      s.history.push({ t: s.minutes, text: 'Collapsed and woke up in a clinic.' });
      events.push({ t: s.minutes, kind: 'need', text: `You collapsed and woke up in a clinic. The bill was ${money(s, fee)}.` });
      left = Math.max(left, 12 * 60); // the rest of the day is gone
    }
    const before = s.minutes;
    s.minutes += step;
    left -= step;
    for (const m of s.inbox.filter((x) => x.at <= s.minutes)) message(s, m.from, m.text, events);
    s.inbox = s.inbox.filter((x) => x.at > s.minutes);
    if (s.applications.length || s.offers.length) resolveApplications(s, events);
    deliverSocial(s, events);
    deliverGroceries(s, events);
    deliverOrders(s, events);
    characterTick(s, events);
    checkMissedShift(s, before, events);
    if (dayOf(before) !== dayOf(s.minutes)) newDay(s, events);
  }
  return events;
}

function checkMissedShift(s: LifeState, before: number, events: LifeEvent[]) {
  const sc = schedule(s);
  if (!sc.days.length) return;
  const d = dayOf(before);
  if (!sc.days.includes(weekday(before)) || s.work.lastShiftDay === d) return;
  // the shift became un-startable during this step
  if (hourOf(before) <= sc.end - 1 && (hourOf(s.minutes) > sc.end - 1 || dayOf(s.minutes) !== d)) {
    s.work.lastShiftDay = d;
    s.work.missed++;
    s.work.streakMissed++;
    s.work.performance = clamp(s.work.performance - 12);
    const what = s.job ? `your shift at ${s.job.place}` : 'your classes';
    events.push({ t: s.minutes, kind: 'missed', text: s.job
      ? `You missed ${what}. ${s.work.streakMissed >= 3 ? '' : `That's ${s.work.streakMissed} in a row: miss 3 and you're let go.`}`.trim()
      : `You missed ${what}.` });
    nudge(s, 'disciplined', -0.03, events);
    if (s.job && s.work.streakMissed >= 3) {
      events.push({ t: s.minutes, kind: 'fired', text: `You've been let go from ${s.job.label.toLowerCase()} after missing three shifts in a row.` });
      s.history.push({ t: s.minutes, text: `Lost the job as ${s.job.label.toLowerCase()}.` });
      s.job = null;
      s.work.owed = 0;
    }
  }
}

function newDay(s: LifeState, events: LifeEvent[]) {
  const day = dayOf(s.minutes);
  const c = content(s);
  const date = dateOf(s.minutes);
  // end-of-day objective: did you end it with more money than you started?
  for (const o of s.objectives) if (o.kind === 'save' && !o.done && o.day === day - 1 && s.money > s.dayStartMoney) completeObjective(s, o, events);
  s.dayStartMoney = s.money;
  if (day > 0) events.push({ t: s.minutes, kind: 'day', text: clockLabel(s.minutes).split(' · ')[0] });

  // the people who work for you: the day's wages (and anyone unpaid gone)
  staffMorning(s, events);
  legacyMorning(s, events);
  // monthly pay on the 25th, family support on the 1st
  if (date.getUTCDate() === 25 && s.job && s.work.owed > 0) {
    const amt = Math.round(s.work.owed / 100) * 100;
    const { net, tax } = paySalary(s, homeCountry(s), amt, `Salary · ${s.job.label}`); // (the income tax in force, out)
    events.push({ t: s.minutes, kind: 'pay', text: `Salary paid: ${money(s, net)}${tax ? ` (${money(s, tax)} income tax)` : ''}`, amount: net });
    s.work.owed = 0;
  }
  if (date.getUTCDate() === 1 && s.card.stage === 'young' && s.card.home.id.startsWith('family')) {
    const amt = c.allowance[s.card.background.id] ?? 0;
    const parent = s.contacts.find((p) => p.relation === 'Mother' || p.relation === 'Father');
    if (amt && parent) {
      earn(s, amt, `From ${parent.name.split(' ')[0]}`);
      events.push({ t: s.minutes, kind: 'pay', text: `${parent.name.split(' ')[0]} sent you ${money(s, amt)}`, amount: amt });
    }
  }

  // rent: Lagos wants a year at once (a warning two weeks out); everywhere else it's monthly (a reminder 5 days out)
  if (s.rent) {
    const yearly = homeCountry(s) === 'nigeria' && !s.rent.monthly, due = Math.round((yearly ? s.rent.yearly : Math.round((s.rent.yearly * (s.rent.monthly ? 1.1 : 1)) / 12 / 100) * 100) * policyShare(homeCountry(s), 'rent_support'));
    if (!s.rent.warned && s.rent.dueDay - day <= (yearly ? 14 : 5) && s.rent.dueDay - day > 0) {
      s.rent.warned = true;
      message(s, 'Landlord', yearly ? `Good morning. Your rent of ${money(s, due)} is due in two weeks.` : `A reminder: this month's rent, ${money(s, due)}, is due in ${s.rent.dueDay - day} days.`, events);
    }
    if (day >= s.rent.dueDay) {
      if (spend(s, due, yearly ? 'Rent (one year)' : 'Rent (this month)')) {
        events.push({ t: s.minutes, kind: 'rent', text: yearly ? `Paid a year's rent: ${money(s, due)}` : `Paid this month's rent: ${money(s, due)}`, amount: -due });
        if (yearly) s.history.push({ t: s.minutes, text: 'Paid a year of rent.' });
        s.rent.dueDay = day + (yearly ? 365 : 30);
        s.rent.warned = false;
      } else if (yearly) {
        // (a year at once you haven't got: the landlord takes it monthly instead, a tenth on top. Most starting lives
        // couldn't find a year's rent, and had no way back)
        s.rent.monthly = true;
        const month = Math.round((s.rent.yearly * 1.1) / 12 / 100) * 100;
        message(s, 'Landlord', `I see you can't pay the year at once. Okay: pay me ${money(s, month)} every month instead (a little extra for the trouble). The first one is due now.`, events);
        if (spend(s, month, 'Rent (this month)')) { events.push({ t: s.minutes, kind: 'rent', text: `Paid this month's rent: ${money(s, month)}`, amount: -month }); s.rent.dueDay = day + 30; s.rent.warned = false; }
      } else if (day === s.rent.dueDay || (day - s.rent.dueDay) % 7 === 0) {
        message(s, 'Landlord', day - s.rent.dueDay >= 21 ? 'This is the last warning. Pay or pack your things.' : 'Your rent is due. I have been patient.', events);
      }
    }
  }

  // business takings, relationships, and the body getting older
  businessDay(s, events);
  ventureDay(s, events); // (the places you own: their managers' takings)
  landDay(s, events); // (your land: the building sites, what's built)
  relationsDay(s, events);
  weddingDay(s, events);
  ageDay(s, events);
  characterDay(s, events);
  bankDay(s, events);
  reviewDay(s, events);
  travelDay(s, events);
  gigsDay(s, events);
  spaceDay(s, events);
  datesDay(s, events);
  eventsDay(s);
  meetupsDay(s, events);
  owesDay(s, events);
  carsDay(s, events);
  petsDay(s, events);

  // someone in your life checks in now and then
  const r = rngFor(s, day * 7 + 3);
  feedDay(s, day);
  if (r.chance(0.45) && s.contacts.length) {
    const p = r.pick(s.contacts);
    const lines: Record<string, string[]> = {
      Mother: ['Have you eaten today?', 'Call me when you are free.', 'I prayed for you this morning.'],
      Father: ['How is work going?', 'Don\'t waste money.', 'Come and see us this weekend.'],
      Friend: voiceOf(homeCountry(s)).friendText,
    };
    // delivered later in the day, not at midnight
    s.inbox.push({ at: day * DAY + r.int(8 * 60, 21 * 60), from: p.name, text: r.pick(lines[p.relation] ?? lines.Friend) });
  }

  // today's life: three things worth doing
  // (yesterday's shift is gone either way: keep it and today's would show twice)
  s.objectives = s.objectives.filter((o) => !o.done && o.day >= day - 1 && o.source.type !== 'daily' && !(o.kind === 'work' && o.day < day));
  for (const o of dailyObjectives(s, day)) s.objectives.push(o);
}

export function message(s: LifeState, from: string, text: string, events: LifeEvent[]) {
  s.messages.unshift({ t: s.minutes, from, text, read: false });
  events.push({ t: s.minutes, kind: 'message', text: `${from}: ${text}` });
}

// --- objectives ------------------------------------------------------------------------------

function dailyObjectives(s: LifeState, day: number): Objective[] {
  const r = rngFor(s, day * 31 + 11);
  const sc = schedule(s);
  const workday = sc.days.includes(day % 7);
  const pool: [Omit<Objective, 'id' | 'progress' | 'done' | 'day' | 'source'>, number][] = [
    [{ kind: 'buy_food', text: 'Buy something to eat from a vendor on the street', target: 1 }, 3],
    [{ kind: 'talk', text: 'Talk to someone new', target: 1 }, 3],
    [{ kind: 'walk', text: 'Walk the length of the street', target: 300 }, 2],
    [{ kind: 'save', text: 'End the day with more money than you started', target: 1 }, 2],
    [{ kind: 'explore', text: 'Look inside three shops', target: 3 }, 2],
  ];
  const family = s.contacts.find((p) => p.relation === 'Mother') ?? s.contacts.find((p) => p.relation === 'Father');
  if (family) pool.push([{ kind: 'call', text: `Call ${family.name.split(' ')[0]}`, target: 1 }, 2]);
  if (!s.job && s.card.education.level !== 'student') pool.push([{ kind: 'apply', text: 'Apply for a job', target: 1 }, 5]);
  else if (s.job) pool.push([{ kind: 'apply', text: 'Apply for a better job', target: 1 }, 0.6]);
  pool.push([{ kind: 'help', text: 'Help someone out today', target: 1 }, 0.8]);
  if (s.card.education.level === 'student') pool.push([{ kind: 'study', text: 'Study for an hour', target: 60 }, 4]);
  const away = !!abroad(s); // (on a trip: no cooking in a hotel room, no Lekki from New York)
  if (!away) pool.push([{ kind: 'cook', text: 'Cook something at home', target: 1 }, 2]);
  pool.push([{ kind: 'post', text: 'Post a photo', target: 1 }, 3]);
  pool.push([{ kind: 'photo_asked', text: 'Ask a stranger to take your picture', target: 1 }, 2]);
  if (s.owned.includes('keyboard')) pool.push([{ kind: 'practice', text: 'Practise the keyboard', target: 1 }, 3]);
  // a day out: more likely on a free day
  if (!away && s.card.region === 'lagos') pool.push([{ kind: 'outing', text: 'Have a day out at Lekki Conservation Centre', target: 1 }, workday ? 0.5 : 2.5]);
  const out: Objective[] = [];
  if (workday && sc.days.length) out.push({ id: `d${day}-work`, kind: 'work', text: s.job ? `Go to work (${String(sc.start).padStart(2, '0')}:00)` : `Go to class (${String(sc.start).padStart(2, '0')}:00)`, target: 1, progress: 0, done: false, day, source: { type: 'career' } });
  // who you are pulls on what you set out to do (a kind person helps, a disciplined one saves)
  for (const p of pool) p[1] *= objectivePull(s, p[0].kind);
  while (out.length < 3 && pool.length) {
    const pick = r.weighted(pool.map((p, i) => [i, p[1]] as const));
    const [o] = pool.splice(pick, 1);
    out.push({ ...o[0], id: `d${day}-${o[0].kind}`, progress: 0, done: false, day, source: { type: 'daily' } });
  }
  return out;
}

function completeObjective(s: LifeState, o: Objective, events: LifeEvent[]) {
  o.done = true;
  o.progress = o.target;
  s.stats.objectivesDone++;
  s.needs.fun = clamp(s.needs.fun + 6);
  events.push({ t: s.minutes, kind: 'objective', text: o.text });
}

/** Something happened in the world that might count towards today's objectives. */
export function progress(s: LifeState, kind: ObjectiveKind, n = 1): LifeEvent[] {
  const events: LifeEvent[] = [];
  for (const o of s.objectives) {
    if (o.done || o.kind !== kind) continue;
    o.progress = Math.min(o.target, o.progress + n);
    if (o.progress >= o.target) completeObjective(s, o, events);
  }
  return events;
}

// --- actions ---------------------------------------------------------------------------------

export interface FoodItem { label: string; price: number; hunger: number; fun?: number; minutes?: number }

export function eat(s: LifeState, item: FoodItem): LifeEvent[] | null {
  if (!spend(s, item.price, item.label)) return null;
  s.needs.hunger = clamp(s.needs.hunger + item.hunger);
  s.stats.mealsBought++;
  s.needs.fun = clamp(s.needs.fun + (item.fun ?? 2));
  return [...advance(s, item.minutes ?? 10), ...progress(s, 'buy_food')];
}

/** Anything bought or paid for on the street: food, a haircut, medicine, a match, a bet. */
export interface Purchase {
  label: string;
  price: number;
  needs?: Partial<Record<NeedKey, number>>;
  health?: number;
  /** how long it takes */
  minutes?: number;
  food?: boolean;
  /** a gamble: chance to win, and the payout multiple */
  bet?: { chance: number; payout: number };
  /** something you keep (unlocks an activity) */
  own?: string;
}

export function buy(s: LifeState, p: Purchase): { events: LifeEvent[]; note?: string } | null {
  if (!spend(s, p.price, p.label)) return null;
  for (const [k, v] of Object.entries(p.needs ?? {})) s.needs[k as NeedKey] = clamp(s.needs[k as NeedKey] + (v as number));
  if (p.health) s.health = clamp(s.health + p.health);
  const events = advance(s, p.minutes ?? 5);
  let note: string | undefined;
  if (p.food) { s.stats.mealsBought++; events.push(...progress(s, 'buy_food')); }
  if (p.own && !s.owned.includes(p.own)) { s.owned.push(p.own); s.history.push({ t: s.minutes, text: p.own.startsWith("outfit:") || p.own.startsWith('jewel:') || p.own.startsWith('hat:') ? `Bought the ${p.label.toLowerCase()}.` : `Bought a ${p.own}.` }); }
  if (p.bet) {
    nudge(s, 'impulsive', 0.015, events);
    const r = rngFor(s, s.minutes * 3 + s.ledger.length);
    if (r.chance(p.bet.chance)) {
      const win = Math.round(p.price * p.bet.payout);
      earn(s, win, `${p.label} (won)`);
      note = `It came in. You won ${money(s, win)}.`;
    } else note = 'It didn\'t come in. One leg cut.';
  }
  return { events, note };
}

/** Ask someone close for money. Family helps more readily than friends, and not every time. */
export function askForMoney(s: LifeState, name: string): { ok: boolean; amount: number; line: string; events: LifeEvent[] } {
  const p = s.contacts.find((c) => c.name === name);
  const r = rngFor(s, s.minutes + name.length * 97);
  const family = !!p && ['Mother', 'Father', 'Brother', 'Sister', 'Wife', 'Husband'].includes(p.relation);
  const recent = s.ledger.filter((t) => t.label.startsWith('From ') && s.minutes - t.t < 14 * DAY).length;
  const chance = (p ? p.closeness / 100 : 0) * (family ? 1 : 0.6) - recent * 0.25;
  const base = { struggling: 5_000, working: 15_000, comfortable: 40_000, wealthy: 120_000 }[s.card.background.id] ?? 10_000;
  const events = advance(s, 5);
  if (!p || !r.chance(Math.max(0.05, chance))) {
    if (p) p.closeness = Math.max(0, p.closeness - 3);
    return { ok: false, amount: 0, line: r.pick(['"Things are hard here too. Maybe next week."', '"I don\'t have it right now, sorry."', '"Again? What happened to the last one?"']), events };
  }
  const amount = Math.round(base * (family ? 1 : 0.4) * r.range(0.5, 1.2) / 500) * 500;
  earn(s, amount, `From ${name.split(' ')[0]}`);
  p.closeness = Math.max(0, p.closeness - 2);
  return { ok: true, amount, line: r.pick(['"I\'ve sent something. Use it well."', '"Take this. Don\'t tell anyone."', '"Check your account."']), events };
}

// --- jobs --------------------------------------------------------------------------------------

/** Jobs you could apply for this week, given your schooling. */
export function openings(s: LifeState): (Job & { pay: number; at: string })[] {
  const c = content(s);
  const week = Math.floor(dayOf(s.minutes) / 7);
  const r = rngFor(s, week * 101 + 7);
  const rank = EDU_RANK[s.card.education.level];
  const field = c.fields.find((f) => f.label === s.card.education.field)?.id;
  const fits = c.jobs.filter((j) => j.id !== 'none' && j.id !== 'failed_biz' && j.id !== 'nysc' && j.id !== s.job?.id
    && j.stages.includes(s.card.stage) && EDU_RANK[j.minEdu] <= rank && (!j.fields || (field && j.fields.includes(field))) && (!j.sex || j.sex === s.card.sex));
  return r.sample(fits, 4).map((j) => ({ ...j, pay: Math.round(r.range(j.income[0], j.income[1]) / 1000) * 1000, at: r.pick(j.place) }));
}

/** Apply for a job; the answer comes in a day or three. */
export function applyForJob(s: LifeState, job: Job & { pay: number; at: string }): LifeEvent[] {
  if (s.applications.some((a) => a.job.id === job.id)) return [];
  const r = rngFor(s, s.minutes + job.id.length);
  s.applications.push({ job: { id: job.id, label: job.label, place: job.at, incomeMonthly: job.pay }, at: s.minutes + r.int(1, 3) * DAY + r.int(9, 16) * 60 - (s.minutes % DAY) });
  return [...advance(s, 20), ...progress(s, 'apply')];
}

function resolveApplications(s: LifeState, events: LifeEvent[]) {
  const c = content(s);
  for (const a of s.applications.filter((x) => x.at <= s.minutes)) {
    const def = c.jobs.find((j) => j.id === a.job.id) ?? (a.job.id === 'astronaut' ? ASTRONAUT : undefined);
    const relevant = Object.keys(def?.skills ?? {}) as Skill[];
    const skill = relevant.length ? relevant.reduce((sum, k) => sum + s.skills[k], 0) / relevant.length : 30;
    const r = rngFor(s, a.at);
    const referred = (selfOf(s).referralUntil ?? 0) > a.at ? 0.22 : 0;
    // (the Corps: the checklist was the selection; tick every box and you're in)
    const ok = a.job.id === 'astronaut' || r.chance(Math.min(0.9, 0.2 + skill / 140 + referred + (s.needs.hygiene > 50 ? 0.05 : -0.1) + (s.dressedDay >= dayOf(a.at) - 3 ? 0.05 : 0)));
    if (ok) {
      s.offers.push({ ...a.job, until: s.minutes + 3 * DAY });
      message(s, a.job.place.replace(/^an? /, '').replace(/^(\w)/, (m) => m.toUpperCase()), `We'd like to offer you the role of ${a.job.label.toLowerCase()}, at ${money(s, a.job.incomeMonthly)} a month. Let us know within three days.`, events);
    } else {
      message(s, 'Recruitment', `Thank you for applying to be a ${a.job.label.toLowerCase()}. We won't be moving forward this time.`, events);
    }
  }
  s.applications = s.applications.filter((x) => x.at > s.minutes);
  s.offers = s.offers.filter((o) => o.until > s.minutes);
}

export function acceptOffer(s: LifeState, index: number): LifeEvent[] {
  const o = s.offers[index];
  if (!o) return [];
  s.offers.splice(index, 1);
  const old = s.job;
  s.job = { id: o.id, label: o.label, place: o.place, incomeMonthly: o.incomeMonthly };
  s.work = { worked: 0, missed: 0, streakMissed: 0, performance: 60, owed: old ? s.work.owed : 0, lastShiftDay: dayOf(s.minutes) };
  s.history.push({ t: s.minutes, text: `Started work as ${o.label.toLowerCase()} at ${o.place}.` });
  return [{ t: s.minutes, kind: 'info', text: `You're now a ${o.label.toLowerCase()}. First shift tomorrow.` }];
}

/**
 * Quit your job: you hand in your notice and you're out today, paid what you're owed so far. (Not from orbit: the
 * mission comes home first.) Then you're looking for work: Jobs → Find work.
 */
export function quitJob(s: LifeState): { ok: boolean; line: string; events: LifeEvent[] } {
  const j = s.job;
  if (!j) return { ok: false, line: 'You don’t have a job to quit.', events: [] };
  if (s.space?.stage === 'orbit') return { ok: false, line: 'Not from the space station: finish the mission and come home first.', events: [] };
  const owed = Math.round(s.work.owed / 100) * 100;
  if (owed > 0) earn(s, owed, `Final pay: ${j.label}`);
  s.history.push({ t: s.minutes, text: `Quit as ${j.label.toLowerCase()} at ${j.place}.` });
  s.job = null;
  s.work = { ...s.work, owed: 0, performance: 60, streakMissed: 0 };
  if (s.space && s.space.stage === 'assigned') s.space.stage = 'none'; // (off the crew: the backup flies)
  return { ok: true, line: `You hand in your notice. That's you done as ${j.label.toLowerCase()} at ${j.place}.${owed > 0 ? ` Your final pay came in: ${money(s, owed)}.` : ''} Jobs → Find work for the next one.`, events: [{ t: s.minutes, kind: 'info', text: `You quit as ${j.label.toLowerCase()}.` }] };
}

/**
 * Leave: days off work you ask for (a trip abroad, a break). No shifts while you're on it, so none missed, and no
 * pay for them (pay is for shifts worked). Back early whenever you like.
 */
export function takeLeave(s: LifeState, days: number): { ok: boolean; line: string } {
  if (!s.job) return { ok: false, line: 'You don’t have a job to take leave from.' };
  if (s.space?.stage === 'orbit') return { ok: false, line: 'Not from the space station.' };
  const from = Math.max(s.minutes, s.work.leaveUntil ?? 0);
  s.work.leaveUntil = (Math.floor(from / DAY) + days) * DAY; // (to the start of the day you're back)
  s.work.streakMissed = 0;
  s.history.push({ t: s.minutes, text: `Took ${days} days' leave from ${s.job.place}.` });
  return { ok: true, line: `Leave approved: ${days} days, back on ${clockLabel(s.work.leaveUntil).split(' · ')[0]}. No shifts, and no pay, till then.` };
}
export function endLeave(s: LifeState): { ok: boolean; line: string } {
  if (!s.work.leaveUntil || s.minutes >= s.work.leaveUntil) return { ok: false, line: 'You’re not on leave.' };
  s.work.leaveUntil = undefined;
  return { ok: true, line: 'Back from leave: your shifts are on again.' };
}
export const onLeave = (s: LifeState) => !!s.work.leaveUntil && s.minutes < s.work.leaveUntil;

/** A phone call keeps a relationship warm. */
export function call(s: LifeState, name: string): LifeEvent[] {
  const p = s.contacts.find((c) => c.name === name);
  if (p) p.closeness = Math.min(100, p.closeness + 4);
  s.needs.social = clamp(s.needs.social + 10);
  return [...advance(s, 12), ...progress(s, 'call')];
}

/**
 * You've arrived at work (or class). The game gets you there for real (book a ride, wait, travel);
 * this just clocks you in. Returns null if there's no shift you can still make.
 */
export function arriveAtWork(s: LifeState): { late: number } | null {
  // (already clocked in today: back after the page reloaded, say: the same arrival, not a later one; before the
  // shift check, which says no once you've clocked in today)
  if (s.work.clockedIn && s.work.lastShiftDay === dayOf(s.minutes)) return { late: s.work.clockedIn.late };
  const sh = shiftNow(s);
  if (!sh) return null;
  const late = Math.max(0, Math.round((hourOf(s.minutes) - sh.start) * 60));
  s.work.lastShiftDay = dayOf(s.minutes);
  s.work.clockedIn = { at: s.minutes, late };
  return { late };
}

/**
 * The rest of the shift, simulated: from now until it ends. `performance` (0..1) is how the
 * representative part you actually played went (fixing the machine, serving customers, the
 * question in class); it moves your standing at work along with being on time and rested.
 */
export function finishShift(s: LifeState, performance = 0.6): { events: LifeEvent[]; summary: string } {
  const c = content(s);
  const sc = schedule(s);
  const events: LifeEvent[] = [];
  const late = (s.work.clockedIn?.late ?? 0) > 15;
  const minutes = Math.max(30, Math.round((sc.end - hourOf(s.minutes)) * 60));
  s.work.clockedIn = null;
  // a long day includes lunch, bought near work
  const half = Math.round(minutes / 2);
  events.push(...advance(s, half, { busy: true }));
  if (minutes > 300) {
    const lunch = cheaper(c.prices.transport * 2.5);
    if (spend(s, lunch, 'Lunch near work')) s.needs.hunger = clamp(s.needs.hunger + 40);
  }
  events.push(...advance(s, minutes - half, { busy: true }));
  s.needs.social = clamp(s.needs.social + 15);
  const r = rngFor(s, dayOf(s.minutes) * 13 + 5);
  const effort = (s.needs.energy / 100) * 0.5 + 0.5;
  s.work.performance = clamp(s.work.performance + ((late ? -6 : 3) + (performance - 0.55) * 10) * effort);
  s.work.worked++;
  if (s.job) (s.experience ??= {})[s.job.id] = Math.max(s.experience[s.job.id] ?? 0, s.work.worked - 1) + 1; // (time in the trade, kept when you change jobs)
  s.work.streakMissed = 0;
  if (!late) { selfOf(s).deeds.diligent++; nudge(s, 'disciplined', 0.004, events); }
  // practice makes you better at the job (and doing it well teaches more)
  const jobDef = s.job ? c.jobs.find((j) => j.id === s.job!.id) ?? (s.job.id === 'astronaut' ? ASTRONAUT : null) : null;
  for (const [k, w] of Object.entries(jobDef?.skills ?? (s.job ? {} : { academics: 20 }))) s.skills[k as Skill] = Math.min(100, s.skills[k as Skill] + (w as number) * 0.012 * (0.7 + performance * 0.6));
  let summary: string;
  if (!s.job) {
    summary = (late ? 'You got to class late and caught the second half.' : performance > 0.7 ? 'A good day of classes. You followed all of it.' : 'You sat through a full day of classes.') + classDay(s, performance, events);
    events.push(...progress(s, 'work'), ...progress(s, 'study', 60));
  } else {
    const perMonth = sc.days.length * 4.33;
    if (sc.pay === 'daily') {
      const take = Math.round((s.job.incomeMonthly / perMonth) * r.range(0.55, 1.45) * (0.7 + s.work.performance / 200) * (0.8 + performance * 0.4) / 100) * 100;
      const { net, tax } = paySalary(s, homeCountry(s), take, s.job.label);
      summary = `${late ? 'You opened late. ' : ''}You made ${money(s, net)} today${tax ? ` after ${money(s, tax)} income tax` : ''}.`;
    } else {
      s.work.owed += s.job.incomeMonthly / perMonth;
      summary = `${late ? 'You were late, and your boss noticed. ' : ''}${performance > 0.75 ? 'A good shift: people noticed the work.' : performance < 0.35 ? 'A rough shift. It showed.' : 'A full shift.'} Pay comes on the 25th.`;
      // a career ladder: good work, enough of it, the skill (and sometimes a certificate) → your manager calls you in
      if (checkPromotion(s)) summary += ' Before you leave, your manager asks to see you in her office.';
      if (s.job.id === 'astronaut' && !inOrbit(s)) afterTraining(s, events);
    }
    events.push(...progress(s, 'work'));
  }
  return { events, summary };
}

/** Headless shortcut: commute, clock in, do the shift (no played part), come back. */
export function goToWork(s: LifeState): { events: LifeEvent[]; summary: string } | null {
  const sh = shiftNow(s);
  if (!sh) return null;
  const c = content(s);
  spend(s, Math.min(cheaper(c.prices.transport * 2), s.money), 'Transport');
  const startAt = Math.max(hourOf(s.minutes) + 0.75, sh.start); // ~45 min commute
  const pre = advance(s, Math.round((startAt - hourOf(s.minutes)) * 60), { busy: true });
  if (!arriveAtWork(s)) return { events: pre, summary: 'By the time you got there, it was over.' };
  const r = finishShift(s, 0.45 + (s.needs.energy / 100) * 0.3);
  if (careerOf(s).promotion) r.summary += ' ' + answerPromotion(s, false).line; // headless: take what's offered
  const back = advance(s, 45, { busy: true });
  return { events: [...pre, ...r.events, ...back], summary: r.summary };
}

/** Sleep until a given hour (default 7am). */
export function sleepUntil(s: LifeState, hour = 7): LifeEvent[] {
  const now = hourOf(s.minutes);
  const hours = now < hour ? hour - now : 24 - now + hour;
  const ev = advance(s, Math.round(hours * 60), { asleep: true });
  s.needs.hygiene = clamp(s.needs.hygiene + 30); // a wash in the morning
  // a comfortable home is a better night's sleep (a good bed, blackout curtains, an AC)
  const comfort = comfortOf(s);
  s.needs.energy = clamp(s.needs.energy + Math.min(15, comfort / 4));
  s.needs.fun = clamp(s.needs.fun + Math.min(8, comfort / 8));
  // warnings about needs the morning already fixed aren't news
  const fixed = NEEDS.filter((n) => s.needs[n.key] >= 20).map((n) => LOW_LINES[n.key]);
  s.lowWarned = s.lowWarned.filter((k) => s.needs[k] < 20);
  return ev.filter((e) => !(e.kind === 'need' && fixed.includes(e.text)));
}

/** A conversation with someone on the street. Returns how it went. */
export function talkToStranger(s: LifeState, name: string): { outcome: 'warm' | 'polite' | 'cold'; line: string; events: LifeEvent[] } {
  const r = rngFor(s, s.minutes);
  const dressed = s.dressedDay === dayOf(s.minutes) ? 0.1 : 0;
  const chance = 0.35 + s.skills.social / 200 + dressed + (s.needs.hygiene < 25 ? -0.25 : 0);
  const roll = r.next();
  const outcome = roll < chance ? 'warm' : roll < chance + 0.35 ? 'polite' : 'cold';
  const lines = {
    warm: ['You end up talking for a while. They tell you where to find the best suya on the street.', 'They laugh at your joke and give you their number.', 'You talk football for ten minutes. They seem to like you.'],
    polite: ['They answer, politely, and go back to their phone.', 'A short chat about the traffic. Nice enough.', 'They nod along, but they\'re busy.'],
    cold: ['They look you up and down and don\'t answer.', '"I no get change," they say, before you\'ve even asked for anything.', 'They wave you off.'],
  }[outcome];
  s.needs.social = clamp(s.needs.social + (outcome === 'warm' ? 18 : outcome === 'polite' ? 8 : 2));
  s.skills.social = Math.min(100, s.skills.social + 0.3);
  s.stats.strangersTalked++;
  const events = advance(s, 6);
  nudge(s, 'outgoing', 0.01, events);
  if (outcome === 'warm') {
    s.contacts.push({ name, relation: 'Acquaintance', closeness: 20, met: s.minutes });
    s.history.push({ t: s.minutes, text: `Met ${name} on the street.` });
  }
  events.push(...progress(s, 'talk'));
  return { outcome, line: r.pick(lines), events };
}
